import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type { Company } from "../types.ts";
import { isNameAvailable } from "../names.ts";
import { NameTakenError } from "../api.ts";
import { Modal } from "./Modal.tsx";

type Props = {
  companies: Company[];
  onCreate: (name: string) => Promise<void>;
  onRename: (id: string, name: string) => Promise<void>;
  onClose: () => void;
};

function NameInput({
  company,
  companies,
  onRename,
}: {
  company: Company;
  companies: Company[];
  onRename: (id: string, name: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState(company.name);
  const valid = isNameAvailable(draft, companies, company.id);

  const commit = () => {
    const name = draft.trim();
    if (!valid || name === company.name) {
      setDraft(company.name);
      return;
    }
    onRename(company.id, name).catch((e: unknown) => {
      toast.error(
        e instanceof NameTakenError
          ? `${name} finns redan`
          : `Kunde inte byta namn på ${company.name}`,
      );
      setDraft(company.name);
    });
  };

  return (
    <input
      type="text"
      aria-label={`Namn på ${company.name}`}
      aria-invalid={!valid}
      value={draft}
      onChange={(e) => setDraft(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      onBlur={commit}
    />
  );
}

export function CompaniesDialog({
  companies,
  onCreate,
  onRename,
  onClose,
}: Props) {
  const [draft, setDraft] = useState("");
  const [creating, setCreating] = useState(false);
  const newRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    newRef.current?.focus();
  }, []);

  const close = () => {
    (document.activeElement as HTMLElement | null)?.blur();
    onClose();
  };

  const canAdd = !creating && isNameAvailable(draft, companies);

  const submit = () => {
    if (!canAdd) return;
    setCreating(true);
    onCreate(draft.trim()).catch((e: unknown) => {
      toast.error(
        e instanceof NameTakenError
          ? `${draft.trim()} finns redan`
          : "Kunde inte skapa företaget",
      );
      setCreating(false);
    });
  };

  return (
    <Modal labelledBy="companies-title" onClose={close}>
      <h2 id="companies-title">Företag</h2>
      <p className="modal-subtitle">
        Varje företag har egen likviditet, egna kostnader och kostnadsställen.
      </p>

      <ul className="cost-center-list">
        {companies.map((c) => (
          <li key={c.id}>
            <NameInput company={c} companies={companies} onRename={onRename} />
          </li>
        ))}
      </ul>

      <form
        className="cost-center-new"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input
          ref={newRef}
          type="text"
          placeholder="Nytt företag"
          aria-label="Nytt företag"
          aria-invalid={
            draft.trim() !== "" && !isNameAvailable(draft, companies)
          }
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
        />
        <button type="submit" disabled={!canAdd}>
          Skapa och öppna
        </button>
      </form>

      <div className="modal-actions">
        <button type="button" className="primary" onClick={close}>
          Stäng
        </button>
      </div>
    </Modal>
  );
}
